import React, { useMemo, useState } from 'react';
import filter from 'lodash/filter';
import _map from 'lodash/map';
import _some from 'lodash/some';

import { setIn, splice } from '@lyearn/core/utils/immutable';
import { useTranslation } from '@lyearn/i18n';
import { PlusFill } from '@lyearn/icons';
import { css } from '@lyearn/style';
import { Box, LinkButton, Typography } from '@lyearn/ui';
import { Modal } from '@/components/Modals';
import {
  Maybe,
  ReminderConfigurationType,
  ReminderConfigurationUnitTypeEnum,
  ReminderTypeEnum,
} from '@/types/schema';

import ReminderDialogRow from './components/ReminderDialogRow';

const dialogSize = css`
  min-width: 65.6rem;
`;

type ReminderDialogProps = {
  onClose: () => void;
  defaultReminderType?: Maybe<ReminderTypeEnum>;
  initialReminderConfig?: Maybe<ReminderConfigurationType[]>;
  onSubmit: (reminderConfig: ReminderConfigurationType[]) => void;
  name?: string;
  description?: string;
};

const DEFAULT_BEFORE_REMINDER: ReminderConfigurationType = {
  reminderType: ReminderTypeEnum.Before,
  unit: ReminderConfigurationUnitTypeEnum.Days,
  value: 1,
};

const DEFAULT_AFTER_REMINDER: ReminderConfigurationType = {
  reminderType: ReminderTypeEnum.After,
  unit: ReminderConfigurationUnitTypeEnum.Days,
  value: 1,
};

function useReminderState(initialReminderConfig: ReminderDialogProps['initialReminderConfig']) {
  const [isValid, setIsValid] = useState(true);
  const [beforeReminderConfig, setBeforeReminderConfig] = useState(() =>
    filter(initialReminderConfig, (config) => config.reminderType === ReminderTypeEnum.Before),
  );
  const [afterReminderConfig, setAfterReminderConfig] = useState(() =>
    filter(initialReminderConfig, (config) => config.reminderType === ReminderTypeEnum.After),
  );
  const reminderConfig = useMemo(
    () => beforeReminderConfig.concat(afterReminderConfig),
    [afterReminderConfig, beforeReminderConfig],
  );

  return {
    isValid,
    setIsValid,
    reminderConfig,
    beforeReminderConfig,
    addBeforeReminder: () => {
      setBeforeReminderConfig((config) => config.concat({ ...DEFAULT_BEFORE_REMINDER }));
      setIsValid(true);
    },
    updateBeforeReminder: (index: number, reminderConfig: Partial<ReminderConfigurationType>) => {
      setBeforeReminderConfig((config) => setIn(config, index, reminderConfig));
      setIsValid(true);
    },
    deleteBeforeReminder: (index: number) => {
      setBeforeReminderConfig((config) => splice(config, index, 1));
      setIsValid(true);
    },
    afterReminderConfig,
    addAfterReminder: () => {
      setAfterReminderConfig((config) => config.concat({ ...DEFAULT_AFTER_REMINDER }));
      setIsValid(true);
    },
    updateAfterReminder: (index: number, reminderConfig: Partial<ReminderConfigurationType>) => {
      setAfterReminderConfig((config) => setIn(config, index, reminderConfig));
      setIsValid(true);
    },
    deleteAfterReminder: (index: number) => {
      setAfterReminderConfig((config) => splice(config, index, 1));
      setIsValid(true);
    },
  };
}

function validate(reminderConfig: Partial<ReminderConfigurationType>[]) {
  return !_some(reminderConfig, (config) => {
    return !config.value;
  });
}

const ReminderDialog: React.FC<ReminderDialogProps> = (props) => {
  const { onClose, initialReminderConfig, onSubmit: onSubmitProps, name, description } = props;

  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const {
    reminderConfig,
    beforeReminderConfig,
    addBeforeReminder,
    updateBeforeReminder,
    deleteBeforeReminder,
    afterReminderConfig,
    addAfterReminder,
    updateAfterReminder,
    deleteAfterReminder,
    isValid,
    setIsValid,
  } = useReminderState(initialReminderConfig);

  const onSubmit = React.useCallback(() => {
    const isValid = validate(reminderConfig);
    setIsValid(isValid);
    if (!isValid) {
      return;
    }

    setIsSubmitting(true);
    onSubmitProps(reminderConfig as ReminderConfigurationType[]);
  }, [onSubmitProps, reminderConfig, setIsValid]);

  return (
    <Modal
      action={{
        discardText: t('Actions.Cancel'),
        text: t('Actions.SaveChanges'),
        onSubmit,
        appearance: 'primary',
        isSubmitting,
      }}
      className="px-32 pb-20"
      classes={{ paper: dialogSize }}
      header={{
        title: name ?? t('Admin:NotificationPreferences.SetReminderDialogTitle'),
        description: description ?? t('Admin:NotificationPreferences.SetReminderDialogDescription'),
        className: 'px-32',
      }}
      showCloseBtn={true}
      onClose={onClose}>
      <Box className="mb-20 overflow-auto">
        <Typography as="p" className="mb-16 mx-8" variant="heading-xs">
          Before Due Date
        </Typography>
        {_map(beforeReminderConfig, (config, index) => {
          return (
            <ReminderDialogRow
              deleteReminder={deleteBeforeReminder}
              index={index}
              reminderConfig={config}
              showError={!isValid}
              updateReminder={updateBeforeReminder}
            />
          );
        })}
        <LinkButton
          className="flex h-40 flex-row items-center justify-start rounded-4 py-2 px-8"
          color={'internal'}
          startIcon={<PlusFill className="h-24 w-24" />}
          typography={{ variant: 'label-02' }}
          onClick={addBeforeReminder}>
          {t('Admin:NotificationPreferences.AddInterval')}
        </LinkButton>
        <Typography as="p" className="mt-24 mb-16 mx-8" variant="heading-xs">
          After Due Date (Overdue Assignments)
        </Typography>
        {_map(afterReminderConfig, (config, index) => {
          return (
            <ReminderDialogRow
              deleteReminder={deleteAfterReminder}
              index={index}
              reminderConfig={config}
              showError={!isValid}
              updateReminder={updateAfterReminder}
            />
          );
        })}
        <LinkButton
          className="flex h-40 flex-row items-center justify-start rounded-4 py-2 px-8"
          color={'internal'}
          startIcon={<PlusFill className="h-24 w-24" />}
          typography={{ variant: 'label-02' }}
          onClick={addAfterReminder}>
          {t('Admin:NotificationPreferences.AddInterval')}
        </LinkButton>
      </Box>
    </Modal>
  );
};

export default ReminderDialog;
